import { ModalProps, useModal, withModalConditionalRender } from '@billy/design-system'
import React, { ReactElement, useCallback, useEffect, useRef } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCurrentUser } from '@modules-deprecated/app/user'
import { isSettingsModulePath } from '@views/settings/utils/isSettingsModulePath'
import { routeToInternalSettings } from '@views/settings/utils/routeToInternalSettings'

import { useEmberCloseModal } from '../../../contexts/emberCloseModalContext'
import { EmberRoute } from '../../../enums/emberRoute'
import { ModalId } from '../../../enums/modalId'
import { useSegment, useZervantNavigation } from '../../../hooks'
import { useEmberRouteUrl } from '../../../hooks/routing/useEmberRouteUrl'
import { useEmberTransitionTo } from '../../../hooks/routing/useEmberTransitionTo'
import { WithTrackingContext } from '../../../types/withTrackingContext'
import { NotOwnerModal } from './elements/NotOwnerModal'
import { OwnerModal } from './elements/OwnerModal'

interface UpgradeSubscriptionModalProps extends WithTrackingContext, ModalProps {
  forceRender?: boolean
  shouldRedirectToZervantPlanPage?: boolean
}

const UpgradeSubscriptionModalComponent = ({
  forceRender,
  id,
  shouldRedirectToZervantPlanPage = false,
  trackingContext,
}: UpgradeSubscriptionModalProps): ReactElement => {
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const { closeEmberModal } = useEmberCloseModal()
  const { transitionTo } = useEmberTransitionTo()
  const { user } = useCurrentUser()
  const isOpeningModalEventFiredRef = useRef(false)
  const { url: settingsSubscriptionsPlansSelectionUrl } = useEmberRouteUrl(
    EmberRoute.SettingsSubscriptionsPlansSelection,
  )
  const { redirectToZervantAccountPlanView } = useZervantNavigation()

  const modalId = id || ModalId.UpgradeSubscriptionModal

  const trackOpeningModal = useCallback(() => {
    if (isOpeningModalEventFiredRef.current) {
      return
    }

    track('Billing Upsell Viewed (Client)', {
      context: trackingContext,
    })

    isOpeningModalEventFiredRef.current = true
  }, [track, trackingContext])

  const { close, isOpen } = useModal(modalId, {
    onClose: () => {
      if (typeof closeEmberModal === 'function') {
        closeEmberModal()
      }
    },
  })

  const isOwner = organization?.ownerUserId === user?.id

  useEffect(() => {
    // `withModalConditionalRender` only mounts this component once `isOpen`/`forceRender` is
    // already true, so tracking on the `onOpen` subscriber would fire too late (registered on
    // mount, after `open()` already ran synchronously). Track on the resulting state instead.
    if ((isOpen || forceRender) && !!user && !!organization) {
      trackOpeningModal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRender, isOpen, organization, user])

  const navigateToUpgradeSubscriptionPlan = useCallback(() => {
    if (shouldRedirectToZervantPlanPage) {
      redirectToZervantAccountPlanView()
      return
    }

    if (isSettingsModulePath()) {
      routeToInternalSettings(EmberRoute.SettingsSubscriptionsPlansSelection)
      return
    }

    if (settingsSubscriptionsPlansSelectionUrl) {
      if (typeof closeEmberModal === 'function') {
        closeEmberModal()
        requestAnimationFrame(() => {
          transitionTo(settingsSubscriptionsPlansSelectionUrl)
        })
        return
      }

      transitionTo(settingsSubscriptionsPlansSelectionUrl)
    }
  }, [
    closeEmberModal,
    redirectToZervantAccountPlanView,
    settingsSubscriptionsPlansSelectionUrl,
    shouldRedirectToZervantPlanPage,
    transitionTo,
  ])

  const closeModal = useCallback(() => {
    if (typeof closeEmberModal === 'function') {
      closeEmberModal()
    }

    close()
  }, [close, closeEmberModal])

  if (!user || !organization) {
    return <></>
  }

  if (!isOwner) {
    return <NotOwnerModal modalId={modalId} forceRender={forceRender} onModalClose={closeModal} />
  }

  return (
    <OwnerModal
      modalId={modalId}
      trackingContext={trackingContext}
      forceRender={forceRender}
      onUpgradeSubscriptionPlan={navigateToUpgradeSubscriptionPlan}
    />
  )
}

export const UpgradeSubscriptionModal = withModalConditionalRender(UpgradeSubscriptionModalComponent)
